import { Button as MantineButton, Group, Stack } from '@mantine/core'
import { BoxStyle } from 'components/Box'
import CronEditor from 'components/CronEditor'
import Field from 'components/Field'
import HeaderControls from 'components/InfinityTable/HeaderControls'
import ListContainer from 'components/InfinityTable/ListContainer'
import RowControls from 'components/InfinityTable/RowControls'
import Sort from 'components/InfinityTable/Sort'
import PrintFormatSelect from 'components/PrintFormatSelect'
import TooltipTrigger from 'components/TooltipTrigger'
import lodash, { get, pick } from 'lodash'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { OnChange } from 'react-final-form-listeners'
import { useTranslation } from 'react-i18next'
import { Badge } from 'reactstrap'
import styled from 'styled-components'

import Button from '../../../1stlog-ui/src/components/Button'
import Checkbox from '../../../1stlog-ui/src/components/Checkbox'
import useRouter from '../../../1stlog-ui/src/components/Router/useRouter'
import { getIsGO } from '../../../1stlog-ui/src/utils/getIsGO'
import { getRawAdditionalServices } from '../../../shipmetrics-shared/containers/OrderEntry/AdditionalServices'
import { useCustomer } from '../Customer'
import { Barcode } from './Barcode'
import { Options } from './Options'
import { getStates, OverallState } from './OverallState'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const HeaderRow = styled.div`
  font-weight: bold;
  text-transform: uppercase;
`

export const IncludedFilters = (props) => {
  const { setIncluded, includedFilters, setFilter } = props
  return [
    <div className="col-md-12 col-12 mx-2 my-2">
      <div className="pull-left mr-2">
        <Field
          name="useDeliveryAddress"
          value={includedFilters.useDeliveryAddress}
        >
          <Checkbox
            label={i18n.t('deliveryAddress', 'Zustelladdresse')}
            value={includedFilters.useDeliveryAddress}
          />
        </Field>
        <OnChange name="useDeliveryAddress">
          {(value) => {
            setIncluded({ name: 'useDeliveryAddress', value })
          }}
        </OnChange>
      </div>
      <div className="pull-left mr-2 ">
        <Field name="usePickupAddress">
          <Checkbox
            label={i18n.t('pickUpAddress', 'Abholadresse')}
            value={includedFilters.usePickupAddress}
          />
        </Field>
        <OnChange name="usePickupAddress">
          {(value) => {
            setIncluded({ name: 'usePickupAddress', value })
          }}
        </OnChange>
      </div>

      <div className="pull-left mr-2">
        <Field name="inactive">
          <Checkbox
            label={i18n.t('showInActive', 'Inaktive Sendungen ausblenden')}
            colorScheme={{ labelColor: '#fff', iconColor: '#000' }}
          />
        </Field>

        <OnChange name="inactive">
          {(value) => {
            setFilter({ name: 'inactive', value })
          }}
        </OnChange>
      </div>
    </div>,
  ]
}

const OrderList = (props) => {
  const {
    setIncluded,
    includedFilters,
    orderHref,
    isSovereign = false,
    items,
  } = props
  const { t } = useTranslation()

  const router = useRouter()

  const { currentCustomer } = useCustomer()

  const isGO = getIsGO()

  useEffect(() => {
    props.applyFilter({ reset: true, onlyFts: false })
  }, [])

  const [isLocalLoading, setLocalLoading] = useState(false)

  const headerControls = <HeaderControls controls={[]} />

  const rowControls = (rowProps) => (
    <RowControls
      {...rowProps}
      controls={[
        {
          label: i18n.t('show', 'Anzeigen'),
          icon: 'fas fa-search',
          to: `/${orderHref}/${lodash.get(rowProps, 'rowIndex')}`,
          info: true,
        },
      ]}
    />
  )

  const mobileCellHeaderItems = ({ rowData }) => {
    const reference = lodash.get(rowData, 'customerReferenceText')
    return [
      {
        width: '50px',
        label: lodash.get(rowData, 'id', '?'),
      },
      {
        flex: '1',
        label: moment(lodash.get(rowData, 'created_at')).format(
          'DD.MM.YYYY HH:mm',
        ),
      },
      {
        flex: '1',
        label: reference
          ? `${i18n.t('shipment_ref', 'Sendungsref')}: ${reference}`
          : i18n.t('without_shipment_reference', 'Ohne Sendungsreferenz'),
      },
    ]
  }

  const mobileCellBody = ({ rowData }) => {
    const {
      senderName1,
      senderName2,
      senderName3,
      senderStreet,
      senderStreetNumber,
      senderStreetNumberAddition,
      senderZip,
      senderCity,
      senderCountry,
      receiverName1,
      receiverName2,
      receiverName3,
      receiverStreet,
      receiverStreetNumber,
      receiverStreetNumberAddition,
      receiverZip,
      receiverCity,
      receiverCountry,
    } = rowData

    return (
      <React.Fragment>
        <Row>
          <Col xs="4">
            <span className="font-weight-bold">ID:</span>
          </Col>
          <Col xs="8">
            <span>{lodash.get(rowData, 'id', '-')}</span>
          </Col>
        </Row>
        <Row>
          <Col xs="4">
            <span className="font-weight-bold">Datum:</span>
          </Col>
          <Col xs="8">
            <span>
              {moment(lodash.get(rowData, 'created_at')).format(
                'DD.MM.YYYY HH:mm',
              )}
            </span>
          </Col>
        </Row>
        <Row>
          <Col xs="4">
            <span className="font-weight-bold">Colis:</span>
          </Col>
          <Col xs="8">
            <span>
              {(lodash.get(rowData, 'colis') || []).length}{' '}
              <i className="fas fa-cube" />
            </span>
          </Col>
        </Row>
        <Row>
          <Col xs="4">
            <span className="font-weight-bold">Barcodes:</span>
          </Col>
          <Col xs="8">
            <span>
              {(lodash.get(rowData, 'colis') || []).map((coli) => (
                <React.Fragment>
                  {lodash.get(coli, 'barcode1')}&nbsp;
                  {coli.isCancelled && <Badge color="danger">Storniert</Badge>}
                  <br />
                </React.Fragment>
              ))}
            </span>
          </Col>
        </Row>
        <Row>
          <Col xs="4">
            <span className="font-weight-bold">Sendungsreferenz:</span>
          </Col>
          <Col xs="8">
            <span>
              {(lodash.get(rowData, 'colis') || []).map((coli) => (
                <React.Fragment>
                  {lodash.get(coli, 'bacoliCustomerReferencercode1')}&nbsp;
                  <br />
                </React.Fragment>
              ))}
            </span>
          </Col>
        </Row>
        <Row>
          <Col xs="4">
            <span className="font-weight-bold">Colireferenz:</span>
          </Col>
          <Col xs="8">
            <div
              {...props}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <span>
                {(lodash.get(rowData, 'colis') || []).map((coli) => {
                  return (
                    <React.Fragment>
                      {lodash.get(coli, 'coliCustomerReference')}
                      <br />
                    </React.Fragment>
                  )
                })}
              </span>
            </div>
          </Col>
        </Row>
        <Row className="mt-1">
          <Col xs="4">
            <span className="font-weight-bold">
              {i18n.t('pickup_address', 'Abholadresse')}:
            </span>
          </Col>
          <Col xs="8">
            <div
              {...props}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <span>
                {senderName1} {senderName1 && <br />}
                {senderName2} {senderName2 && <br />}
                {senderName3} {senderName3 && <br />}
                {senderStreet} {senderStreetNumber} {senderStreetNumberAddition}
                <br />
                {senderZip} {senderCity} {senderCountry}
              </span>
            </div>
          </Col>
        </Row>
        <Row className="mt-1">
          <Col xs="4">
            <span className="font-weight-bold">
              {i18n.t('receiver_address', 'Empfangsadresse')}:
            </span>
          </Col>
          <Col xs="8">
            <div
              {...props}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <span>
                {receiverName1} {receiverName1 && <br />}
                {receiverName2} {receiverName2 && <br />}
                {receiverName3} {receiverName3 && <br />}
                {receiverStreet} {receiverStreetNumber}{' '}
                {receiverStreetNumberAddition}
                <br />
                {receiverZip} {receiverCity} {receiverCountry}
              </span>
            </div>
          </Col>
        </Row>
        <div className="p-2">{rowControls(props)}</div>
      </React.Fragment>
    )
  }

  const mobileColumn = [
    {
      headerRenderer: (renderprops) => (
        <HeaderRow className="d-flex justify-content-between align-items-center">
          <Sort
            {...props}
            sorts={props.sort}
            sortKey="id"
            applyFilter={props.applyFilter}
            setSort={props.setSort}
            sortableColumns={props.sortableColumns}
            label="#"
          />
          {headerControls}
        </HeaderRow>
      ),
    },
  ]

  const columns = [
    {
      label: 'ID',
      dataKey: 'id',
      sortKey: 'id',
      colWidth: 2,
    },
    {
      label: i18n.t('date', 'Datum'),
      dataKey: 'created_at',
      sortKey: 'created_at',
      colWidth: 25,
      cellRenderer: (props) => (
        <div {...props}>
          <span>
            {moment(props.cellData).isValid()
              ? moment(props.cellData).format('DD.MM.YYYY HH:mm')
              : '-'}
          </span>
        </div>
      ),
    },
    {
      label: i18n.t('pickup_from_label', 'Abholung ab'),
      dataKey: 'pickupTimeFrom',
      sortKey: 'pickupTimeFrom',
      colWidth: 40,
      cellRenderer: (props) => (
        <div {...props}>
          <span>
            {moment(props.cellData).isValid()
              ? moment(props.cellData).format('DD.MM.YYYY HH:mm')
              : '-'}
          </span>
        </div>
      ),
    },
    {
      label: i18n.t('status', 'Status'),
      dataKey: 'overallState',
      colWidth: 20,
      sortKey: 'overallState',
      cellRenderer: (props) => {
        const { cellData } = props
        // Abgeholt, Zugestellt, Abholfehler, Zustellefehler ,Unterwegs, Beauftrag
        return (
          <div {...props}>
            <OverallState state={cellData} />
          </div>
        )
      },
    },
    // {
    //   label: i18n.t('colis', 'Colis'),
    //   dataKey: 'colis',
    //   colWidth: 60,
    //   flexGrow: 0,
    //   cellRenderer: (props) => {
    //     return (
    //       <div {...props}>
    //         {Array.isArray(props.cellData) ? (
    //           <span>
    //             {props.cellData.length} <i className="fas fa-cube" />
    //           </span>ds
    //         ) : null}
    //       </div>
    //     )
    //   },
    // },

    {
      label: i18n.t('barcodes', 'Barcodes'),
      dataKey: 'colis',
      sortKey: 'colis.barcode1',
      colWidth: 140,
      cellRenderer: (props) => {
        return (
          <div {...props} className="pt-1">
            {Array.isArray(props.cellData) ? (
              <Stack spacing={5}>
                {props.cellData.map((coli) => (
                  <Barcode
                    coli={coli}
                    zip={props.rowData.receiverZip}
                    key={coli.id}
                    isPickup={get(props.rowData.sla_object, 'isPickup')}
                    coliState={get(props.rowData.coliStates, coli.barcode1)}
                    trackingHash={props.rowData.trackingHash}
                    weight={coli.weight ?? null}
                  />
                ))}
              </Stack>
            ) : null}
          </div>
        )
      },
    },
    {
      label: i18n.t('customer_ref_short', 'Sendungsref.'),
      dataKey: 'customerReferenceText',
      sortKey: 'customerReferenceText',
    },
    {
      label: i18n.t('coliReference', 'Colireferenz'),
      dataKey: 'colis',
      sortKey: 'colis.coliCustomerReference',
      cellRenderer: (props) => {
        return (
          <div
            {...props}
            className="pt-1"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {Array.isArray(props.cellData) ? (
              <span>
                {props.cellData.map((coli) => {
                  return (
                    <React.Fragment>
                      {lodash.get(coli, 'coliCustomerReference')}
                      <br />
                    </React.Fragment>
                  )
                })}
              </span>
            ) : null}
          </div>
        )
      },
    },
    {
      label: i18n.t('SLA', 'SLA'),
      dataKey: [
        'sla',
        'additional_services',
        'cashondeliveryAmount',
        'cashondeliveryCurrency',
      ],
      colWidth: 20,
      sortKey: 'sla',
      cellRenderer: (props) => {
        const { cellData } = props
        const services = getRawAdditionalServices()
        return (
          <div {...props}>
            <span>{lodash.get(cellData, 'sla')}</span>{' '}
            {!lodash.isEmpty(cellData.additional_services) && (
              <TooltipTrigger
                element={
                  <div style={{ maxWidth: 500 }}>
                    <React.Fragment>
                      <h6 style={{ color: 'white' }}>
                        {i18n.t('additional_services', 'Zusatzservices')}
                      </h6>
                      {lodash.map(cellData.additional_services, (service) => (
                        <React.Fragment>
                          <span>
                            {lodash.find(
                              services,
                              (s) => s.name === service.name,
                            )?.label || service.description}{' '}
                            {service.name === 'COD' &&
                              `${lodash.get(
                                cellData,
                                'cashondeliveryAmount',
                              )} ${lodash.get(
                                cellData,
                                'cashondeliveryCurrency',
                              )}`}
                          </span>
                          <br />
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  </div>
                }
              >
                <i className="fas fa-question-circle" />
              </TooltipTrigger>
            )}
          </div>
        )
      },
    },
    {
      label: i18n.t('pickUpAddress', 'Abholadresse'),
      dataKey: [
        'senderName1',
        'senderName2',
        'senderName3',
        'senderStreet',
        'senderStreetNumber',
        'senderStreetNumberAddition',
        'senderZip',
        'senderCountry',
        'senderCity',
      ],
      cellRenderer: (props) => {
        const { cellData } = props

        return (
          <div
            {...props}
            className="py-1"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {Array.isArray(props.cellData) ? (
              <span>
                {cellData.senderName1} {cellData.senderName1 && <br />}
                {cellData.senderName2} {cellData.senderName2 && <br />}
                {cellData.senderName3} {cellData.senderName3 && <br />}
                {cellData.senderStreet} {cellData.senderStreetNumber}{' '}
                {cellData.senderStreetNumberAddition}
                <br />
                {cellData.senderZip} {cellData.senderCity}{' '}
                {cellData.senderCountry}
              </span>
            ) : null}
          </div>
        )
      },
    },
    {
      label: i18n.t('deliveryAddress', 'Zustelladresse'),
      dataKey: [
        'receiverName1',
        'receiverName2',
        'receiverName3',
        'receiverStreet',
        'receiverStreetNumber',
        'receiverStreetNumberAddition',
        'receiverZip',
        'receiverCountry',
        'receiverCity',
      ],
      cellRenderer: (props) => {
        const { cellData } = props

        return (
          <div
            {...props}
            className="py-1"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {Array.isArray(props.cellData) ? (
              <span>
                {cellData.receiverName1} {cellData.receiverName1 && <br />}
                {cellData.receiverName2} {cellData.receiverName2 && <br />}
                {cellData.receiverName3} {cellData.receiverName3 && <br />}
                {cellData.receiverStreet} {cellData.receiverStreetNumber}{' '}
                {cellData.receiverStreetNumberAddition}
                <br />
                {cellData.receiverZip} {cellData.receiverCity}{' '}
                {cellData.receiverCountry}
              </span>
            ) : null}
          </div>
        )
      },
    },
    {
      label: '',
      disableSort: true,
      dataKey: 'id',
      width: 10,
      cellRenderer: (props) => (
        <div {...props}>
          <Options
            order={props.rowData}
            setLoading={setLocalLoading}
            cronOrdersEnabled={lodash.get(currentCustomer, 'cronOrdersEnabled')}
            openLink={`/${orderHref}/${lodash.get(props, 'cellData')}`}
            routerProps={pick(props, ['history', 'location', 'match'])}
          />
          <span hidden>
            <MantineButton color="blue" size="xs">
              <Group>
                <i className="fas fa-eye" />
                <div>{i18n.t('show_capfirst', 'Anzeigen')}</div>
              </Group>
            </MantineButton>
            <Button
              href={`/${orderHref}/${lodash.get(props, 'cellData')}`}
              to={`/${orderHref}/${lodash.get(props, 'cellData')}`}
              primary
              sm
            >
              <i className="fas fa-search" />{' '}
              {i18n.t('show_capfirst', 'Anzeigen')}
            </Button>
            {lodash.get(currentCustomer, 'cronOrdersEnabled', false) && (
              <CronEditor
                spanClass="ml-2"
                order={props.rowData}
                setLoading={setLocalLoading}
                buttonElement={
                  <Button
                    className="mr-2"
                    tooltip={i18n.t(
                      'cron_editor_order',
                      'Dauerauftrag erstellen',
                    )}
                    info
                    sm
                  >
                    <i className="fas fa-clock" />
                  </Button>
                }
              />
            )}
          </span>
        </div>
      ),
    },
  ]

  const servicelevelOptions = lodash.map(global.servicelevel, (value, key) => ({
    value: key,
    label: lodash.get(value, 'name'),
  }))

  const filter = [
    {
      name: 'date',
      label: i18n.t('daterange_order_Creation_filter', 'Auftragserstellung'),
      hideLabel: true,
      type: 'daterange',
      colWidth: 12,
      autoFocus: false,
    },
    {
      name: 'pickupTime',
      label: i18n.t('daterange', 'Abholdatum ab'),
      hideLabel: true,
      type: 'daterange',
      colWidth: 12,
      autoFocus: false,
    },
    {
      name: 'sla',
      label: i18n.t('servicelevel', 'Servicelevel'),
      hideLabel: true,
      type: 'multiValue',
      options: servicelevelOptions,
      placeholder: 'Servicelevel',
      colWidth: 12,
      autoFocus: false,
    },
    {
      name: 'barcode1',
      label: i18n.t('label_barcode1', 'Barcode 1'),
      type: 'value',
      colWidth: 12,
      autoFocus: true,
    },
    {
      name: 'colireference',
      label: i18n.t('coliReference', 'Colireferenz'),
      type: 'value',
      colWidth: 12,
      autoFocus: true,
    },
    {
      name: 'shipmentreference',
      label: i18n.t('customer_ref_short', 'Sendungsref.'),
      type: 'value',
      colWidth: 12,
      autoFocus: true,
    },

    {
      name: 'name1',
      label: i18n.t('name1', 'Name 1'),
      type: 'value',
      colWidth: 12,
    },

    {
      name: 'name2',
      label: i18n.t('name2', 'Name 2'),
      type: 'value',
      colWidth: 6,
    },
    {
      name: 'name3',
      label: i18n.t('name3', 'Name 3'),
      type: 'value',
      colWidth: 6,
    },
    {
      name: 'street',
      label: i18n.t('street', 'Strasse'),
      type: 'value',
      colWidth: 6,
    },
    {
      name: 'streetNumber',
      label: i18n.t('streetNumber_short', 'Nr.'),
      type: 'value',
      colWidth: 3,
    },
    {
      name: 'streetNumberAddition',
      label: i18n.t('streetNumberAddition_short', 'Zus.'),
      type: 'value',
      colWidth: 3,
    },

    { name: 'zip', label: i18n.t('plz', 'PLZ'), type: 'value', colWidth: 4 },
    {
      name: 'city',
      label: i18n.t('city', 'Stadt'),
      type: 'value',
      colWidth: 4,
    },
    {
      name: 'country',
      label: i18n.t('country_short', 'Land'),
      type: 'value',
      colWidth: 4,
    },
    {
      name: 'fulltext',
      label: i18n.t(
        'included_fts_orderlist',
        'Suche nach Barcode, Name, Adresse',
      ),
      type: 'fulltext',
      exludeForDetail: true,
      showForFts: true,
      colWidth: 12,
    },
    {
      name: 'state',
      label: i18n.t('state', 'Status'),
      type: 'select',
      colWidth: 12,
      placeholder: i18n.t('state_filter_any', 'Beliebig'),
      options: lodash.map(getStates(), (label, value) => ({
        value,
        label,
      })),
    },
    {
      name: 'includedFilter',
      type: 'component',
      showForFts: true,
      component: (
        <IncludedFilters
          setIncluded={setIncluded}
          includedFilters={includedFilters}
          setFilter={props.setFilter}
          isSovereign={isSovereign}
        />
      ),
    },
  ]

  let prefillValues = {}
  lodash.forEach(props.initFilters, (filter) => {
    prefillValues[filter.field] = filter.value
  })

  const getRowHeight = ({ index }) => {
    const item = items[index]
    let rowHeight = 80
    const defaultRowHeight = 20
    if (item) {
      if (item.senderName2 || item.receiverName2) {
        rowHeight += defaultRowHeight
      }
      if (item.senderName3 || item.receiverName3) {
        rowHeight += defaultRowHeight
      }
      const coliCount = lodash.size(item.colis)
      const coliRowHeight = coliCount * 30
      if (coliRowHeight > rowHeight) {
        rowHeight = coliRowHeight
      }
      // console.log('rowCount', { rowCount, coliCount })
    }

    return rowHeight
  }

  const getMobileRowHeight = ({ index }) => {
    const item = items[index]
    let rowCount = 20

    if (item) {
      if (item.senderName2) {
        rowCount++
      }
      if (item.receiverName2) {
        rowCount++
      }
      if (item.senderName3) {
        rowCount++
      }
      if (item.receiverName3) {
        rowCount++
      }

      if (lodash.size(item.colis.coliCustomerReference || []) > 2) {
        rowCount += lodash.size(item.colis.coliCustomerReference) - 1
      }

      item.colis.forEach((coli) => {
        if (coli.barcode1) rowCount++
      })
    }

    return rowCount * 20
  }

  const [selectedIds, setSelectedIds] = useState([])

  const clearSelection = () => {
    setSelectedIds([])
  }

  const getIsSelectable = useCallback((item) => {
    return (
      lodash.get(item, 'printAllowed') &&
      lodash.every(
        lodash.get(item, 'colis'),
        (coli) => !lodash.get(coli, 'isCancelled'),
      )
    )
  }, [])

  const printableSelectedIds = useMemo(() => {
    const notAllowed = lodash.map(
      lodash.filter(lodash.values(items), (i) => !getIsSelectable(i)),
      (i) => String(lodash.get(i, 'id')),
    )
    return lodash.difference(selectedIds, notAllowed)
  }, [selectedIds, items])

  return (
    <Wrapper>
      <ListContainer
        {...props}
        initialValues={{ ...includedFilters, ...prefillValues }}
        isLoading={props.isLoading || isLocalLoading}
        columns={columns}
        filter={filter}
        title={orderHref ? null : 'Aufträge'}
        // topMarginOrder
        rowHeight={getRowHeight}
        mobileColumn={mobileColumn}
        mobileRowHeight={getMobileRowHeight}
        mobileRowHeightCollapsed={35}
        mobileCellBody={mobileCellBody}
        mobileCellHeaderItems={mobileCellHeaderItems}
        customHeaders={{ allIds: true }}
        enableSelection
        onChangeSelection={setSelectedIds}
        getIsSelectable={getIsSelectable}
        selectedIds={selectedIds}
        selectionLimit={50}
      />

      {!lodash.isEmpty(printableSelectedIds) && (
        <BoxStyle className="mx-2 mb-2 mt-n1">
          <div className="d-flex">
            <div className="mr-2">
              <PrintFormatSelect
                orderIds={printableSelectedIds}
                text={i18n.t(
                  'print_selected_orders',
                  'Markierte Aufträge drucken',
                )}
              />
            </div>
            <div>
              <Button onClick={clearSelection}>
                {i18n.t('cancel', 'Abbrechen')}
              </Button>
            </div>
          </div>
        </BoxStyle>
      )}
    </Wrapper>
  )
}
export default OrderList
